import React, {useEffect} from 'react';
import ImageSvgHandles from './handles';
import {SvgImage} from '../../../../data/models/LessonContent/ContentFreeform';
import {BASELINE_SVG_CANVAS_WIDTH} from '../SvgCanvas';

interface Props {
  image: SvgImage;
  source: string;
  aspect: number;
  updateImageOptions?: (options: SvgImage) => void;
  onClick?: () => void;
  id: string;
  width: number;
}

const Image = React.memo(
  ({image, source, aspect, updateImageOptions, onClick, id, width}: Props) => {
    const [imageOptions, setImageOptions] = React.useState<SvgImage>({
      x: 0,
      y: 0,
      width: 0,
    });
    const [position, setPosition] = React.useState({
      active: false,
      offset: {x: 0, y: 0},
      initial: {x: 0, y: 0},
    });

    useEffect(() => {
      setImageOptions({...image});
    }, [image]);

    const onImageClick = (e: any) => {
      if (onClick) {
        e.stopPropagation();
        onClick();
      }
    };

    const handlePointerDown = (e: any) => {
      if (!updateImageOptions) return;
      const el = e.target;
      const x = e.clientX;
      const y = e.clientY;
      el.setPointerCapture(e.pointerId);
      setPosition({
        ...position,
        active: true,
        offset: {
          x,
          y,
        },
        initial: {
          x: imageOptions.x,
          y: imageOptions.y,
        },
      });
    };

    const handlePointerMove = (e: any) => {
      const x = e.clientX;
      const y = e.clientY;
      if (position.active) {
        setImageOptions({
          ...imageOptions,
          x: position.initial.x - (position.offset.x - x) / getScaleFactor(),
          y: position.initial.y - (position.offset.y - y) / getScaleFactor(),
        });
      }
    };

    const handlePointerUp = () => {
      setPosition({
        ...position,
        active: false,
      });
      if (updateImageOptions) {
        updateImageOptions(imageOptions);
      }
    };

    const getScaleFactor = () => {
      const scale = (width * 100) / BASELINE_SVG_CANVAS_WIDTH;
      return scale / 100;
    };

    const onSetWidth = (width: number, x: number, y: number) => {
      if (width < 50) return;
      setImageOptions({
        ...imageOptions,
        width,
        x,
        y,
      });
      if (updateImageOptions) {
        updateImageOptions({
          ...imageOptions,
          width,
          x,
          y,
        });
      }
    };

    return (
      <g>
        <defs>
          <rect
            id={`rect-${id}`}
            x={imageOptions.x}
            y={imageOptions.y}
            width={imageOptions.width}
            height={imageOptions.width / aspect}
            rx={10}
          />
          <clipPath id={`clip-${id}`}>
            <use xlinkHref={`#rect-${id}`} />
          </clipPath>
        </defs>
        <use xlinkHref="#rect" />
        {updateImageOptions && (
          <rect
            x={imageOptions.x - 1}
            y={imageOptions.y - 1}
            width={imageOptions.width + 2}
            height={imageOptions.width / aspect + 2}
            stroke="#53B483"
            strokeWidth={2}
            fill="transparent"
          />
        )}
        <image
          x={imageOptions.x}
          y={imageOptions.y}
          onPointerDown={handlePointerDown}
          clipPath={`url(#clip-${id})`}
          onPointerUp={handlePointerUp}
          onPointerMove={handlePointerMove}
          width={imageOptions.width}
          cursor={updateImageOptions ? 'move' : onClick ? 'pointer' : ''}
          href={source}
          onClick={onImageClick}
        />
        {updateImageOptions && (
          <ImageSvgHandles
            width={imageOptions.width}
            aspectRatio={aspect}
            x={imageOptions.x}
            y={imageOptions.y}
            setWidth={onSetWidth}
          />
        )}
      </g>
    );
  },
);

export default Image;
